import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { PoolDataService } from '../../services/pool-data.service';
import { PoolEditComponent } from '../pool-edit/pool-edit.component';
import {BehaviorSubject, Observable, merge, of, Subscription} from 'rxjs';
import { Pool } from '../../models/pool';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {MessageService} from "primeng/api";
import { TableStateService } from '../../../../shared/services/table-state.service';

@Component({
  selector: 'app-pool-list',
  template: require('./pool-list.component.html'),
  styles: [require('./pool-list.component.sass')],

})
export class PoolListComponent implements OnInit, OnDestroy {

  @ViewChild(PoolEditComponent) poolEdit: PoolEditComponent;

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [50,100,250,500],
    exportFilename: 'pools'
  }

  columns: any[] ;

  languageChangeSubscription: Subscription;

  private readonly refreshPoolList$ = new BehaviorSubject(undefined);
  pools$: Observable<Pool[] | null>;
  projectId: number;

  constructor(
    private poolDataService: PoolDataService,
    @Inject('ProjectService') private projectService: any,
    private confirmationDialogService: ConfirmationDialogService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private messageService: MessageService,
    public tableState: TableStateService
  ) {
    this.pools$ = this.refreshPoolList$.pipe(
      switchMap(() => merge(
        of(null),
        this.poolDataService.getAllPools(this.projectId))
      ))
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.refreshPoolTable();
    })
  }

  ngOnInit(): void {
    this.projectId = this.projectService.getSelectedProjectId();
    this.refreshPoolTable();
  }

  refreshPoolTable = () => {
    this.columns = [{
      header: this.translate.instant('I18N_GENERAL.NAME'),
      field: 'name',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_POOL.LIMIT_PER_SIM'),
      type: 'text',
      field: 'simPoolConfig.limit',
    }, {
      header: this.translate.instant('I18N_POOL.NORMAL_PRICE'),
      field: 'simPoolConfig.normalPrice',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_POOL.PRICE_AFTER_LIMIT'),
      field: 'simPoolConfig.limitPrice',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_POOL.NATIONAL'),
      field: 'simPoolConfig.national',
      type: 'boolean'
    }, {
      header: this.translate.instant('I18N_GENERAL.ACTIONS'),
      disableSorting: true,
      disableSearch: true
    }]
  }


  async addPool() {
    await this.poolEdit.showModal()
  }

  async editPool(poolId: number) {
    const pool = await this.poolDataService.getPool(poolId, this.projectId);
    await this.poolEdit.showModal(pool)
  }


  reload = () => {
    this.refreshPoolList$.next(undefined);
  }

  onSavePool = async (pool: Pool) => {
    try {
      if (pool && pool.id) {
        await this.poolDataService.updatePool(pool, this.projectId);
        this.messageService.add({
          severity:'success',
          summary: this.translate.instant("I18N_TRAIN.CREATE_TRAIN"),
          detail: this.translate.instant("I18N_TRAIN.TRAIN_CREATION_SUCCESSFUL")
        });
      } else {
        await this.poolDataService.createPool(pool, this.projectId);
        this.messageService.add({
          severity:'success',
          summary: this.translate.instant("I18N_TRAIN.CREATE_TRAIN"),
          detail: this.translate.instant("I18N_TRAIN.TRAIN_CREATION_SUCCESSFUL")
        });
      }
      this.reload();
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant(pool && pool.id ? "I18N_POOL.EDIT_POOL": "I18N_POOL.CREATE_POOL"),
        detail: error && error.message
      });
    }

  }

  deletePool = async (pool: Pool) => {
    if (pool && pool.id) {
      const confirmed = await this.confirmationDialogService.confirm(
        `${this.translate.instant('I18N_POOL.DELETE_POOL')} ${pool.name}`,
        this.translate.instant('I18N_POOL.ARE_YOU_SURE_TO_DELETE_THIS_POOL?'),
        'danger',
        this.translate.instant('I18N_POOL.DELETE_POOL'),
        this.translate.instant('I18N_GENERAL.CANCEL'),
        "lg");
      if (confirmed) {
        try {
          await this.poolDataService.deletePool(pool.id, this.projectId);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_POOL.DELETE_POOL'),
            detail:`Pool ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.reload();
        } catch (error) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant('I18N_POOL.DELETE_POOL'),
            detail: error && error.message
          });
        }
      }
    }
  }
  ngOnDestroy() {
    this.languageChangeSubscription.unsubscribe();
  }

}
